/**
 * <arcero-filter-bar> — client-side category/color/size/price filtering,
 * sorting, density toggle, active-filter pills and empty state for the
 * arcero-collection grid. No network requests, no URL params.
 *
 * Reads target items via the `target-selector` attribute (default
 * `[data-arcero-filter-item]`). Expects data-category, data-colors,
 * data-sizes, data-price, data-title, data-created on each item — see the
 * doc comment in blocks/arcero-filter-bar.liquid for the full contract
 * (data-sizes and the --arcero-cols grid variable are not yet produced by
 * sections/arcero-collection.liquid).
 */
class ArceroFilterBar extends HTMLElement {
  connectedCallback() {
    this.targetSelector = this.getAttribute('target-selector') || '[data-arcero-filter-item]';
    this.items = Array.from(document.querySelectorAll(this.targetSelector));
    this.originalOrder = this.items.slice();
    this.gridContainer = this.items.length ? this.items[0].parentElement : null;

    this.activeCategory = '';
    this.activeColors = new Set();
    this.activeSizes = new Set();
    this.activePriceChips = new Set();
    this.activeSort = '';

    this.categoryButtons = Array.from(this.querySelectorAll('[data-arcero-category-chip]'));
    this.colorButtons = Array.from(this.querySelectorAll('[data-arcero-color-swatch]'));
    this.sizeButtons = Array.from(this.querySelectorAll('[data-arcero-size-chip]'));
    this.priceButtons = Array.from(this.querySelectorAll('[data-arcero-price-chip]'));
    this.sortSelect = this.querySelector('[data-arcero-sort-select]');
    this.countEl = this.querySelector('[data-arcero-filter-count]');
    this.densityButtons = Array.from(this.querySelectorAll('[data-arcero-density]'));
    this.filterToggle = this.querySelector('[data-arcero-filter-toggle]');
    this.filterBadge = this.querySelector('[data-arcero-filter-badge]');
    this.panel = this.querySelector('[data-arcero-panel]');
    this.activePillsEl = this.querySelector('[data-arcero-active-pills]');
    this.resetButtons = Array.from(this.querySelectorAll('[data-arcero-reset]'));
    this.applyButton = this.querySelector('[data-arcero-apply]');

    this.categoryButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectCategory(button));
    });

    this.colorButtons.forEach((button) => {
      button.addEventListener('click', () => this.toggleSetFilter(this.activeColors, button));
    });

    this.sizeButtons.forEach((button) => {
      button.addEventListener('click', () => this.toggleSetFilter(this.activeSizes, button));
    });

    this.priceButtons.forEach((button) => {
      button.addEventListener('click', () => this.toggleSetFilter(this.activePriceChips, button));
    });

    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', () => {
        this.activeSort = this.sortSelect.value;
        this.applySort();
      });
    }

    this.densityButtons.forEach((button) => {
      button.addEventListener('click', () => this.setDensity(button));
    });

    if (this.filterToggle && this.panel) {
      this.filterToggle.addEventListener('click', () => this.togglePanel());
    }

    if (this.applyButton) {
      this.applyButton.addEventListener('click', () => this.closePanel());
    }

    this.resetButtons.forEach((button) => {
      button.addEventListener('click', () => this.resetFilters());
    });

    if (this.items.length > 0) {
      this.applyFilters();
    }
  }

  selectCategory(button) {
    this.activeCategory = button.dataset.value || '';

    this.categoryButtons.forEach((candidate) => {
      const isActive = candidate === button;
      candidate.classList.toggle('is-active', isActive);
      candidate.setAttribute('aria-pressed', String(isActive));
    });

    this.applyFilters();
  }

  toggleSetFilter(set, button) {
    if (set.has(button)) {
      set.delete(button);
      button.setAttribute('aria-pressed', 'false');
      button.classList.remove('is-on');
    } else {
      set.add(button);
      button.setAttribute('aria-pressed', 'true');
      button.classList.add('is-on');
    }

    this.applyFilters();
  }

  setDensity(button) {
    this.densityButtons.forEach((candidate) => {
      candidate.classList.toggle('is-on', candidate === button);
    });
    document.documentElement.style.setProperty('--arcero-cols', button.dataset.cols);
  }

  togglePanel() {
    const open = this.panel.classList.toggle('is-open');
    this.filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  closePanel() {
    this.panel.classList.remove('is-open');
    this.filterToggle.setAttribute('aria-expanded', 'false');
  }

  resetFilters() {
    this.activeColors.clear();
    this.activeSizes.clear();
    this.activePriceChips.clear();

    [...this.colorButtons, ...this.sizeButtons, ...this.priceButtons].forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
      button.classList.remove('is-on');
    });

    this.applyFilters();
  }

  matches(item) {
    const category = item.dataset.category || '';
    const colors = (item.dataset.colors || '').split(' ').filter(Boolean);
    const sizes = (item.dataset.sizes || '').split(' ').filter(Boolean);
    const price = Number(item.dataset.price || 0);

    if (this.activeCategory && category !== this.activeCategory) {
      return false;
    }

    if (this.activeColors.size > 0) {
      const hasColor = Array.from(this.activeColors).some((button) => colors.includes(button.dataset.value));
      if (!hasColor) return false;
    }

    if (this.activeSizes.size > 0) {
      const hasSize = Array.from(this.activeSizes).some((button) => sizes.includes(button.dataset.value));
      if (!hasSize) return false;
    }

    if (this.activePriceChips.size > 0) {
      const inRange = Array.from(this.activePriceChips).some((button) => {
        const min = Number(button.dataset.min || 0);
        const max = button.dataset.max ? Number(button.dataset.max) : Infinity;
        return price >= min && price <= max;
      });
      if (!inRange) return false;
    }

    return true;
  }

  applyFilters() {
    let visibleCount = 0;

    this.items.forEach((item) => {
      const visible = this.matches(item);
      item.hidden = !visible;
      if (visible) {
        visibleCount += 1;
      }
    });

    this.applySort();
    this.updateCount(visibleCount);
    this.updateBadge();
    this.updateActivePills();
    this.toggleEmptyState(visibleCount);
  }

  applySort() {
    const order = this.activeSort ? this.sortedItems() : this.originalOrder;

    order.forEach((item) => {
      item.parentElement?.appendChild(item);
    });
  }

  sortedItems() {
    const [key, direction] = this.activeSort.split('-');
    const multiplier = direction === 'descending' ? -1 : 1;

    return this.items.slice().sort((a, b) => {
      const valueA = this.sortValue(a, key);
      const valueB = this.sortValue(b, key);

      if (valueA < valueB) return -1 * multiplier;
      if (valueA > valueB) return 1 * multiplier;
      return 0;
    });
  }

  sortValue(item, key) {
    switch (key) {
      case 'price':
        return Number(item.dataset.price || 0);
      case 'created':
        return Number(item.dataset.created || 0);
      default:
        return 0;
    }
  }

  updateCount(count) {
    if (!this.countEl) return;
    const label = count === 1 ? 'produkt' : 'produkter';
    this.countEl.textContent = `${count} ${label}`;
  }

  updateBadge() {
    if (!this.filterBadge) return;
    const n = this.activeColors.size + this.activeSizes.size + this.activePriceChips.size;
    this.filterBadge.hidden = n === 0;
    this.filterBadge.textContent = String(n);
  }

  updateActivePills() {
    if (!this.activePillsEl) return;

    const pills = [];
    this.activeColors.forEach((button) => pills.push({ set: this.activeColors, button }));
    this.activeSizes.forEach((button) => pills.push({ set: this.activeSizes, button }));
    this.activePriceChips.forEach((button) => pills.push({ set: this.activePriceChips, button }));

    this.activePillsEl.innerHTML = '';

    pills.forEach(({ set, button }) => {
      const label = button.textContent.trim();

      const pill = document.createElement('span');
      pill.className = 'arcero-filter-bar__pill';
      pill.textContent = `${label} `;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.setAttribute('aria-label', 'Fjern filter');
      remove.textContent = '✕';
      remove.addEventListener('click', () => {
        set.delete(button);
        button.setAttribute('aria-pressed', 'false');
        button.classList.remove('is-on');
        this.applyFilters();
      });

      pill.appendChild(remove);
      this.activePillsEl.appendChild(pill);
    });

    if (pills.length > 0) {
      const clearAll = document.createElement('button');
      clearAll.type = 'button';
      clearAll.className = 'arcero-filter-bar__pill-clear';
      clearAll.textContent = 'Ryd alle';
      clearAll.addEventListener('click', () => this.resetFilters());
      this.activePillsEl.appendChild(clearAll);
    }
  }

  toggleEmptyState(visibleCount) {
    if (!this.gridContainer) return;

    let emptyEl = this.gridContainer.querySelector('[data-arcero-empty-state]');

    if (visibleCount === 0) {
      if (!emptyEl) {
        emptyEl = document.createElement('li');
        emptyEl.setAttribute('data-arcero-empty-state', '');
        emptyEl.className = 'arcero-filter-bar__empty';
        emptyEl.innerHTML =
          '<h3>Ingen match</h3>' +
          '<p>Der er ikke noget der passer på alle filtrene lige nu. Prøv at fjerne størrelse eller farve.</p>' +
          '<button type="button" data-arcero-empty-reset>Nulstil filtre</button>';
        this.gridContainer.appendChild(emptyEl);
        emptyEl.querySelector('[data-arcero-empty-reset]').addEventListener('click', () => this.resetFilters());
      }
    } else if (emptyEl) {
      emptyEl.remove();
    }
  }
}

customElements.define('arcero-filter-bar', ArceroFilterBar);
